/**
 * Centralized manager for Preline UI components initialization
 */

let isPrelineLoaded = false
let initializationQueue: (() => void)[] = []

/**
 * Check if Preline is loaded and ready
 * (Internal function, not exported)
 */
function isPrelineReady(): boolean {
  return isPrelineLoaded && !!window.HSStaticMethods
}

/**
 * Set Preline loading status
 */
export function setPrelineLoaded(loaded: boolean): void {
  isPrelineLoaded = loaded
  if (loaded && initializationQueue.length > 0) {
    // Execute all queued initializations
    initializationQueue.forEach(callback => callback())
    initializationQueue = []
  }
}

/**
 * Safe initialization of Preline components
 */
export function initializePreline(selector?: string): void {
  const doInit = () => {
    try {
      if (window.HSStaticMethods) {
        if (selector) {
          // Initialize only for the specified selector
          window.HSStaticMethods.autoInit(selector)
        } else {
          // Initialize all components
          window.HSStaticMethods.autoInit()
        }
      }
    } catch (error) {
      console.warn('Failed to initialize Preline components:', error)
    }
  }

  if (isPrelineReady()) {
    // Use setTimeout to avoid DOM issues
    setTimeout(doInit, 0)
  } else {
    // Add to queue for execution after Preline loads
    initializationQueue.push(doInit)
  }
}

/**
 * Debounced version of initialization to avoid multiple calls
 */
let debounceTimer: ReturnType<typeof setTimeout> | null = null

export function debouncedInitializePreline(delay: number = 100): void {
  if (debounceTimer) {
    clearTimeout(debounceTimer)
  }
  
  debounceTimer = setTimeout(() => {
    initializePreline()
    debounceTimer = null
  }, delay)
} 